import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Clock, Star, Trash2 } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../lib/api';
import { workOrderHref } from '../lib/desk';
import { useDepartments } from '../lib/automation';
import { useAuth } from '../lib/auth';
import { formatDate, formatMinutes, formatRelative, toDateInput } from '../lib/format';
import { RATING_LABEL } from '../lib/reports';
import type { PendingSurvey, TicketField, TimeEntries, WorkOrderDetail } from '../lib/types';
import { useToast } from './toast';
import { Alert, Button, Card, CardHeader, cn, Field, Input, Select, Textarea } from './ui';

// ---------------------------------------------------------------------------
// Satisfaction
// ---------------------------------------------------------------------------

export function RatingStars({ rating, className }: { rating: number; className?: string }) {
  return (
    <span className={cn('inline-flex shrink-0 items-center gap-0.5', className)} role="img" aria-label={`${rating} out of 5`} title={RATING_LABEL[rating]}>
      {[1, 2, 3, 4, 5].map((value) => (
        <Star key={value} className={cn('size-3.5', value <= rating ? 'fill-amber-400 text-amber-400' : 'text-slate-300')} />
      ))}
    </span>
  );
}

/** The requester rates a resolved ticket here; staff see how it was rated. */
export function SurveyCard({ ticket }: { ticket: WorkOrderDetail }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState('');

  const respond = useMutation({
    mutationFn: () => api.post(`/work-orders/${ticket.id}/survey`, { rating, comment }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['work-order', ticket.id] });
      void queryClient.invalidateQueries({ queryKey: ['my-surveys'] });
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
      toast.success('Thanks for the feedback');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const survey = ticket.survey;
  if (!survey) return null;

  if (survey.canRespond) {
    const submit = (event: FormEvent) => {
      event.preventDefault();
      if (rating) respond.mutate();
    };
    const shown = hover || rating;
    return (
      <Card className="ring-2 ring-brand-100">
        <CardHeader title="How did we do?" description="Your rating helps the team improve." />
        <form onSubmit={submit} className="space-y-3 p-5">
          <div className="flex flex-wrap items-center gap-1" role="radiogroup" aria-label="Rating" onMouseLeave={() => setHover(0)}>
            {[1, 2, 3, 4, 5].map((value) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={rating === value}
                aria-label={`${value} out of 5: ${RATING_LABEL[value]}`}
                onClick={() => setRating(value)}
                onMouseEnter={() => setHover(value)}
                className="rounded p-0.5 focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:outline-none"
              >
                <Star className={cn('size-7 transition-colors', value <= shown ? 'fill-amber-400 text-amber-400' : 'text-slate-300')} />
              </button>
            ))}
            <span className="ml-2 text-sm text-slate-600">{shown ? RATING_LABEL[shown] : ''}</span>
          </div>
          <Textarea rows={2} maxLength={2000} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Anything we should know? (optional)" />
          <Button type="submit" variant="primary" disabled={!rating} loading={respond.isPending}>
            Send rating
          </Button>
        </form>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader title="Satisfaction" />
      <div className="space-y-1 p-5 text-sm">
        {survey.rating ? (
          <>
            <p className="flex items-center gap-2">
              <RatingStars rating={survey.rating} />
              <span className="text-slate-700">{RATING_LABEL[survey.rating]}</span>
            </p>
            {survey.comment && <p className="whitespace-pre-wrap text-slate-600">“{survey.comment}”</p>}
            <p className="text-xs text-slate-500">Rated {formatRelative(survey.respondedAt)}</p>
          </>
        ) : (
          <p className="text-slate-500">
            {survey.expired ? 'The requester didn’t rate this ticket.' : `Survey sent ${formatRelative(survey.sentAt)}. Waiting for the requester.`}
          </p>
        )}
      </div>
    </Card>
  );
}

/** Resolved tickets waiting for the signed-in person's rating. */
export function PendingSurveysAlert({ className }: { className?: string }) {
  const surveys = useQuery({ queryKey: ['my-surveys'], queryFn: () => api.get<PendingSurvey[]>('/my/surveys') });
  if (!surveys.data?.length) return null;
  return (
    <Alert tone="blue" icon={<Star />} title={surveys.data.length === 1 ? 'How did we do? Rate your resolved ticket' : `How did we do? Rate ${surveys.data.length} resolved tickets`} className={className}>
      <ul className="mt-1 space-y-0.5">
        {surveys.data.map((survey) => (
          <li key={survey.id}>
            <Link to={workOrderHref(survey.service ?? 'IT', survey.id)} className="font-medium underline">
              #{survey.number} {survey.title}
            </Link>
          </li>
        ))}
      </ul>
    </Alert>
  );
}

// ---------------------------------------------------------------------------
// Time logged
// ---------------------------------------------------------------------------

export function TimeCard({ ticket }: { ticket: WorkOrderDetail }) {
  const { me, can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const time = useQuery({ queryKey: ['work-order-time', ticket.id], queryFn: () => api.get<TimeEntries>(`/work-orders/${ticket.id}/time`) });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ hours: '', minutes: '', workDate: toDateInput(new Date()), note: '' });
  const total = Math.round(Number(form.hours || 0) * 60) + Math.round(Number(form.minutes || 0));

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['work-order-time', ticket.id] });
    void queryClient.invalidateQueries({ queryKey: ['work-order', ticket.id] });
  };
  const add = useMutation({
    mutationFn: () => api.post(`/work-orders/${ticket.id}/time`, { minutes: total, workDate: form.workDate, note: form.note }),
    onSuccess: () => {
      refresh();
      setForm({ hours: '', minutes: '', workDate: toDateInput(new Date()), note: '' });
      setOpen(false);
      toast.success('Time logged');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const remove = useMutation({
    mutationFn: (entryId: string) => api.delete(`/work-orders/${ticket.id}/time/${entryId}`),
    onSuccess: refresh,
    onError: (err) => toast.error(errorMessage(err)),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (total > 0) add.mutate();
  };
  const entries = time.data?.entries ?? [];

  return (
    <Card>
      <CardHeader
        title="Time"
        description={`${formatMinutes(time.data?.totalMinutes ?? ticket.timeSpentMinutes)} logged`}
        actions={
          ticket.permissions.logTime &&
          !open && (
            <Button size="sm" icon={<Clock />} onClick={() => setOpen(true)}>
              Log time
            </Button>
          )
        }
      />
      {open && (
        <form onSubmit={submit} className="space-y-3 border-b border-slate-100 p-5">
          <div className="grid grid-cols-3 gap-2">
            <Field label="Hours">
              <Input type="number" min={0} max={24} value={form.hours} autoFocus onChange={(e) => setForm({ ...form, hours: e.target.value })} />
            </Field>
            <Field label="Minutes">
              <Input type="number" min={0} max={59} value={form.minutes} onChange={(e) => setForm({ ...form, minutes: e.target.value })} />
            </Field>
            <Field label="Date">
              <Input type="date" required max={toDateInput(new Date())} value={form.workDate} onChange={(e) => setForm({ ...form, workDate: e.target.value })} />
            </Field>
          </div>
          <Input value={form.note} maxLength={500} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="What did you do? (optional)" aria-label="Note" />
          <div className="flex justify-end gap-2">
            <Button type="button" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={total < 1} loading={add.isPending}>
              {total > 0 ? `Log ${formatMinutes(total)}` : 'Log time'}
            </Button>
          </div>
        </form>
      )}
      {entries.length === 0 ? (
        <p className="px-5 py-4 text-sm text-slate-500">No time logged yet.</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {entries.map((entry) => (
            <li key={entry.id} className="flex items-start gap-3 px-5 py-2.5 text-sm">
              <span className="w-14 shrink-0 font-medium text-slate-900 tabular-nums">{formatMinutes(entry.minutes)}</span>
              <div className="min-w-0 flex-1">
                <p className="text-slate-700">
                  {entry.userName} <span className="text-slate-400">· {formatDate(new Date(`${entry.workDate}T12:00:00`))}</span>
                </p>
                {entry.note && <p className="text-xs text-slate-500">{entry.note}</p>}
              </div>
              {ticket.permissions.logTime && (entry.userId === me?.user.id || can('ADMIN')) && (
                <button
                  type="button"
                  onClick={() => remove.mutate(entry.id)}
                  disabled={remove.isPending}
                  className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-red-600"
                  aria-label={`Remove ${formatMinutes(entry.minutes)} logged by ${entry.userName}`}
                >
                  <Trash2 className="size-4" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Custom ticket fields
// ---------------------------------------------------------------------------

function FieldInput({
  field,
  value,
  disabled,
  onSave,
}: {
  field: TicketField;
  value: string | null;
  disabled: boolean;
  onSave(value: string | boolean | null): void;
}) {
  const [draft, setDraft] = useState(value ?? '');
  useEffect(() => setDraft(value ?? ''), [value]);
  const hint = field.description ?? undefined;

  if (field.type === 'SELECT') {
    return (
      <Field label={field.name} hint={hint}>
        <Select value={value ?? ''} disabled={disabled} onChange={(e) => onSave(e.target.value || null)}>
          <option value="">Not set</option>
          {field.options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
          {value && !field.options.includes(value) && <option value={value}>{value}</option>}
        </Select>
      </Field>
    );
  }
  if (field.type === 'CHECKBOX') {
    return (
      <Field label={field.name} hint={hint}>
        <Select value={value ?? ''} disabled={disabled} onChange={(e) => onSave(e.target.value === '' ? null : e.target.value === 'true')}>
          <option value="">Not set</option>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </Select>
      </Field>
    );
  }
  return (
    <Field label={field.name} hint={hint}>
      <Input
        type={field.type === 'NUMBER' ? 'number' : 'text'}
        value={draft}
        disabled={disabled}
        maxLength={500}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          if (draft !== (value ?? '')) onSave(draft.trim() === '' ? null : draft);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
        }}
      />
    </Field>
  );
}

/** Fields the school added (e.g. damage type) that apply to this ticket's department. */
export function TicketFieldsCard({ ticket }: { ticket: WorkOrderDetail }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const fields = useQuery({ queryKey: ['ticket-fields', ticket.service], queryFn: () => api.get<TicketField[]>('/ticket-fields', { service: ticket.service }) });
  const departments = useDepartments();
  const save = useMutation({
    mutationFn: (values: Record<string, string | boolean | null>) => api.put(`/work-orders/${ticket.id}/fields`, { values }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['work-order', ticket.id] }),
    onError: (err) => toast.error(errorMessage(err)),
  });

  const valueOf = (fieldId: string) => ticket.fieldValues.find((v) => v.fieldId === fieldId)?.value ?? null;
  const lineage = ticket.departmentId ? [ticket.departmentId, ...(departments.data?.find((d) => d.id === ticket.departmentId)?.ancestorIds ?? [])] : [];
  const applicable = (fields.data ?? []).filter(
    (field) => field.departmentIds.length === 0 || field.departmentIds.some((id) => lineage.includes(id)) || valueOf(field.id) !== null,
  );
  if (applicable.length === 0) return null;

  return (
    <Card>
      <CardHeader title="Ticket fields" />
      <div className="space-y-4 p-5">
        {applicable.map((field) => (
          <FieldInput
            key={field.id}
            field={field}
            value={valueOf(field.id)}
            disabled={!ticket.permissions.editFields || save.isPending}
            onSave={(value) => save.mutate({ [field.id]: value })}
          />
        ))}
      </div>
    </Card>
  );
}
